import React from 'react';
import DistributionChart from './DistributionChart';

interface MetricData {
  name: string;
  description: string;
  value: number;
  unit: string;
  optimalRange: {
    min: number;
    max: number;
  };
  distributionData: Array<{ x: number; y: number }>;
  benchmarkStats: {
    min: number;
    max: number;
    average: number;
    percentile: number;
  };
}

interface MetricDistributionCardProps {
  metric: MetricData;
  isHighlighted?: boolean;
}

const MetricDistributionCard: React.FC<MetricDistributionCardProps> = ({
  metric,
  isHighlighted = false,
}) => {
  const getPerformanceIndicator = () => {
    const { value, optimalRange, benchmarkStats } = metric;
    
    if (value >= optimalRange.min && value <= optimalRange.max) {
      return { status: 'optimal', color: '#10b981', message: 'Optimal range' };
    } else if (benchmarkStats.percentile > 75) {
      return { status: 'good', color: '#3b82f6', message: `Better than ${benchmarkStats.percentile}% of publishers` };
    } else if (benchmarkStats.percentile > 50) {
      return { status: 'average', color: '#f59e0b', message: 'Above average' };
    } else {
      return { status: 'below', color: '#ef4444', message: 'Below average' };
    }
  };

  const getComparisonText = () => {
    const { percentile } = metric.benchmarkStats;
    if (percentile > 90) return 'exceptional';
    if (percentile > 75) return 'strong';
    if (percentile > 50) return 'good';
    if (percentile > 25) return 'fair';
    return 'needs improvement';
  };

  const performance = getPerformanceIndicator();
  const comparison = getComparisonText();

  return (
    <div className={`metric-distribution-card ${isHighlighted ? 'highlighted' : ''}`}>
      <div className="metric-header">
        <div className="metric-title">
          <h3>{metric.name}</h3>
          <p className="metric-description">{metric.description}</p>
        </div>
        <div className="metric-status">
          <div 
            className="status-indicator"
            style={{ backgroundColor: performance.color }}
          />
          <span className="status-text">{performance.message}</span>
        </div>
      </div>

      <div className="metric-value-display">
        <div className="primary-value">
          <span className="value">{metric.value}</span>
          <span className="unit">{metric.unit}</span>
        </div>
        <div className="comparison-badge" style={{ color: performance.color }}>
          {comparison}
        </div>
      </div>

      <div className="chart-section">
        <DistributionChart
          data={metric.distributionData}
          publisherValue={metric.value}
          title={metric.name}
          unit={metric.unit}
          minValue={metric.benchmarkStats.min}
          maxValue={metric.benchmarkStats.max}
          optimalMin={metric.optimalRange.min}
          optimalMax={metric.optimalRange.max}
        />
      </div>

      <div className="metric-insights">
        <div className="benchmark-stats">
          <div className="stat-group">
            <span className="stat-label">Optimal range:</span>
            <span className="stat-value">
              {metric.optimalRange.min}{metric.unit} - {metric.optimalRange.max}{metric.unit}
            </span>
          </div>
          <div className="stat-group">
            <span className="stat-label">Industry average:</span>
            <span className="stat-value">
              {metric.benchmarkStats.average.toFixed(1)}{metric.unit}
            </span>
          </div>
          <div className="stat-group">
            <span className="stat-label">Range:</span>
            <span className="stat-value">
              {metric.benchmarkStats.min}{metric.unit} - {metric.benchmarkStats.max}{metric.unit}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricDistributionCard;